import { Injectable } from '@angular/core';
import { BaseMapProvider, MapProvider, MarkerOptions, RouteRequest, RouteResult } from './map-provider.service';
import { GoogleMapsService } from './google-maps-service.service';
import { RouteConsistencyService } from './route-consistency.service';
import { osService } from '../osService.service';

declare var google: any;

@Injectable({
  providedIn: 'root'
})
export class GoogleMapsProviderService extends BaseMapProvider {
  private directionsService: any;
  private directionsRenderer: any;
  private userInfoWindow: any;
  private currentInfoWindow: any;

  constructor(
    private googleMapsService: GoogleMapsService,
    private osService: osService,
    private routeConsistencyService: RouteConsistencyService
  ) {
    super();
  }

  async initializeMap(element: HTMLElement, options: any): Promise<any> {
    if (typeof google === "undefined") {
      this.googleMapsService.initMapUrl();
      return new Promise((resolve) => {
        setTimeout(() => this.initializeMap(element, options).then(resolve), 1500);
      });
    }

    return new Promise((resolve) => {
      this.googleMapsService.getGoogleMapsLoaded().subscribe((isLoaded: boolean) => {
        if (isLoaded) {
          this.setupServices();
          this.createMapInstance(element, options);
          resolve(this.map);
        }
      });
    });
  }

  private setupServices(): void {
    this.userInfoWindow = new google.maps.InfoWindow();
    this.currentInfoWindow = new google.maps.InfoWindow();
    this.directionsService = new google.maps.DirectionsService();
    this.directionsRenderer = new google.maps.DirectionsRenderer();
  }

  private createMapInstance(element: HTMLElement, options: any): void {
    const mapOptions = {
      zoom: options.zoom || 15,
      zoomControl: false,
      center: options.center,
      clickableIcons: false,
      fullscreenControl: false,
      mapTypeControl: false,
      streetViewControl: false,
      mapId: "4504f8b37365c3d0",
      headingInteractionEnabled: false,
      tiltInteractionEnabled: false,
    };

    this.map = new google.maps.Map(element, mapOptions);
    this.directionsRenderer.setMap(this.map);
  }

  createMarker(position: any, options: MarkerOptions = {}): any {
    const markerOptions = {
      position: new google.maps.LatLng(position.lat || position.latitude, position.lng || position.longitude),
      map: this.map,
      icon: options.icon,
      title: options.title,
      clickable: options.clickable !== false,
      draggable: options.draggable || false,
    };

    const marker = new google.maps.Marker(markerOptions);
    this.addMarker(marker);
    return marker;
  }

  removeMarker(marker: any): void {
    if (marker) {
      google.maps.event.clearInstanceListeners(marker);
      marker.setMap(null);
      this.removeMarkerFromList(marker);
    }
  }

  clearMarkers(markers: any[]): void {
    markers.forEach(marker => this.removeMarker(marker));
  }

  async calculateRoute(request: RouteRequest): Promise<RouteResult> {
    return new Promise(async (resolve, reject) => {
      try {
        // Vérifier si Google Maps est chargé et initialiser les services si nécessaire
        if (typeof google === "undefined" || !google.maps) {
          this.googleMapsService.initMapUrl();
          await new Promise(resolve => setTimeout(resolve, 1500));
        }

        // Initialiser les services si ce n'est pas déjà fait
        if (!this.directionsService) {
          this.setupServices();
        }

        // Normalisation et validation de la requête
        const normalizedRequest = this.routeConsistencyService.normalizeRouteRequest(request);
        if (!this.routeConsistencyService.validateRouteRequest(normalizedRequest)) {
          reject(new Error('Invalid route request'));
          return;
        }

        // Log pour le débogage
        this.routeConsistencyService.logRouteRequest(normalizedRequest, 'GoogleMaps');

        // Normalisation des coordonnées
        const origin = this.routeConsistencyService.normalizeCoordinates(normalizedRequest.origin);
        const destination = this.routeConsistencyService.normalizeCoordinates(normalizedRequest.destination);

        // Construction de la requête Google Maps avec paramètres standardisés
        const googleRequest: any = {
          origin: new google.maps.LatLng(origin.lat, origin.lng),
          destination: new google.maps.LatLng(destination.lat, destination.lng),
          travelMode: google.maps.TravelMode.DRIVING,
          unitSystem: google.maps.UnitSystem.METRIC,
          avoidHighways: request.avoidHighways || false,
          avoidTolls: request.avoidTolls || false,
          avoidFerries: request.avoidFerries || false,
          optimizeWaypoints: request.optimizeWaypoints || false
        };

        // Ajout des waypoints si présents
        if (request.waypoints && request.waypoints.length > 0) {
          googleRequest.waypoints = request.waypoints.map(wp => ({
            location: new google.maps.LatLng(
              this.normalizeCoordinates(wp.location || wp).lat,
              this.normalizeCoordinates(wp.location || wp).lng
            ),
            stopover: wp.stopover !== false
          }));
        }

        this.directionsService.route(googleRequest, (result: any, status: any) => {
          if (status === google.maps.DirectionsStatus.OK) {
            this.directionsRenderer.setDirections(result);
            this.directionsRenderer.setOptions({
              preserveViewport: true,
              polylineOptions: {
                strokeColor: "#4088CD",
                strokeWeight: 4,
              },
              markerOptions: {
                icon: {
                  path: google.maps.SymbolPath.CIRCLE,
                  scale: 6,
                  fillColor: "#4088CD",
                  fillOpacity: 1,
                  strokeColor: "#4088CD",
                  strokeWeight: 0,
                },
              },
            });

            const route = result.routes[0];
            let totalDurationInSeconds = 0;
            route.legs.forEach((leg: any) => {
              totalDurationInSeconds += leg.duration?.value || 0;
            });

            const totalDistance = route.legs.reduce((acc: number, leg: any) => acc + leg.distance!.value, 0);

            const routeResult: RouteResult = {
              distance: this.osService.standardizeDistance(totalDistance),
              duration: {
                hours: Math.floor(totalDurationInSeconds / 3600),
                minutes: Math.floor((totalDurationInSeconds % 3600) / 60)
              },
              route: result
            };

            // Validation et log du résultat
            this.routeConsistencyService.validateRouteResult(routeResult, origin, destination);
            this.routeConsistencyService.logRouteResult(routeResult, 'GoogleMaps');

            resolve(routeResult);
          } else {
            reject(new Error(`Directions request failed due to ${status}`));
          }
        });
      } catch (error) {
        reject(error);
      }
    });
  }

  updateMarkerPosition(marker: any, position: any): void {
    if (marker) {
      const newPosition = new google.maps.LatLng(position.lat || position.latitude, position.lng || position.longitude);
      marker.setPosition(newPosition);
    }
  }

  setMapCenter(map: any, center: any): void {
    if (map) {
      const position = new google.maps.LatLng(center.lat, center.lng);
      map.setCenter(position);
    }
  }

  destroyMap(map: any): void {
    if (this.directionsRenderer) {
      this.directionsRenderer.setMap(null);
    }
    if (map) {
      google.maps.event.clearInstanceListeners(map);
    }
    this.clearAllMarkers();
    this.map = null;
  }

  // Méthodes spécifiques à Google Maps
  calculateBearing(lat1: number, lon1: number, lat2: number, lon2: number): number {
    if (this.map) {
      const startLat = google.maps.geometry.spherical.computeHeading(
        new google.maps.LatLng(lat1, lon1),
        new google.maps.LatLng(lat2, lon2)
      );
      return (startLat + 360) % 360;
    }
    return 0;
  }

  calculateDistance(point1: any, point2: any): number {
    return google.maps.geometry.spherical.computeDistanceBetween(point1, point2);
  }
} 